import APITemplate from '@api/APITemplate'
import { diffTimeMinutes, formatQueryParams } from '@utils/Format'
import store from '@state/store'
import { MINUTES_TIMEOUT_REQUESTS } from '@constants/Numbers'
import con from '@services/connection'
import { budgets } from '@modules/financial-planning/constants/URL'

export function getQueryString(params) {
  const query = formatQueryParams(params || {})
  return query === '?' ? '' : query
}

class APIFinancialPlanningBudgets extends APITemplate {
  constructor() {
    super()

    this.reqCarregar = {}
  }

  _shouldSkipLoad(clientId, forceUpdate) {
    if (!clientId) {
      return { status: 400, message: 'Cliente não informado.' }
    }

    const storedClientId = store.getters['financialPlanning/budgetsClientId']
    const sameClient = String(storedClientId) === String(clientId)

    if (!forceUpdate && sameClient) {
      const shouldUpdate = diffTimeMinutes(
        store.getters['financialPlanning/budgetsLastUpdate'],
        MINUTES_TIMEOUT_REQUESTS
      )

      if (!shouldUpdate) {
        return {
          status: 200,
          value: store.getters['financialPlanning/budgets'] || [],
        }
      }
    }

    if (store.getters['financialPlanning/budgetsLoading'] && sameClient) {
      return {
        status: 200,
        value: store.getters['financialPlanning/budgets'] || [],
      }
    }

    return null
  }

  _handleResponse(response, clientId) {
    if (response && response.status === 200) {
      const items = Array.isArray(response.value) ? response.value : []
      store.dispatch('financialPlanning/setBudgets', { items, clientId })
    } else {
      store.dispatch('financialPlanning/resetBudgets')
    }
  }

  async _executeCarregar(clientId, requestKey, requestUrl) {
    store.dispatch('financialPlanning/setBudgetsLoading', true)
    this.reqCarregar[requestKey] = con.get(requestUrl)

    try {
      const response = await this.reqCarregar[requestKey]
      this._handleResponse(response, clientId)
      return response
    } catch (error) {
      this._handleResponse(null, clientId)
      throw error
    } finally {
      store.dispatch('financialPlanning/setBudgetsLoading', false)
      delete this.reqCarregar[requestKey]
    }
  }

  async carregar({ clientId, params = {}, forceUpdate = false } = {}) {
    const skipResult = this._shouldSkipLoad(clientId, forceUpdate)
    if (skipResult) return skipResult

    const query = getQueryString(params)
    const requestKey = query ? `${clientId}:${query}` : `${clientId}`

    if (this.reqCarregar[requestKey]) {
      return this.reqCarregar[requestKey]
    }

    const requestUrl = `${budgets.list(clientId)}${query}`
    const response = await this._executeCarregar(clientId, requestKey, requestUrl)

    return (
      response || {
        status: 500,
        message: 'Não foi possível carregar o orçamento. Tente novamente.',
      }
    )
  }

  async salvar({ clientId, budget }) {
    if (!clientId) {
      return {
        status: 400,
        message: 'Cliente não informado.',
      }
    }

    if (!budget || typeof budget !== 'object') {
      return {
        status: 400,
        message: 'Dados do orçamento inválidos.',
      }
    }

    const payload = {
      data: JSON.stringify(budget),
    }

    const response = await con.post(budgets.list(clientId), payload)

    if (response && response.status === 200) {
      const value = Array.isArray(response.value)
        ? response.value[0]
        : response.value

      if (value && typeof value === 'object') {
        store.dispatch('financialPlanning/addBudget', {
          item: value,
          clientId,
        })
      } else {
        await this.carregar({ clientId, forceUpdate: true })
      }
    }

    return response || {}
  }

  async atualizar({ budgetId, budget, clientId } = {}) {
    if (!budgetId) {
      return {
        status: 404,
        message: 'Orçamento não encontrado.',
      }
    }

    if (!budget || typeof budget !== 'object') {
      return {
        status: 400,
        message: 'Dados do orçamento inválidos.',
      }
    }

    const payload = {
      data: JSON.stringify(budget),
    }

    const response = await con.put(
      budgets.list(clientId) + `/${budgetId}`,
      payload
    )

    if (response && response.status === 200) {
      const value = Array.isArray(response.value)
        ? response.value[0]
        : response.value

      const targetClientId =
        clientId || store.getters['financialPlanning/budgetsClientId']

      if (value && typeof value === 'object' && targetClientId) {
        store.dispatch('financialPlanning/updateBudget', {
          item: value,
          clientId: targetClientId,
        })
      } else if (targetClientId) {
        await this.carregar({ clientId: targetClientId, forceUpdate: true })
      }
    }

    return response || {}
  }

  async remover({ budgetId, clientId } = {}) {
    if (!budgetId) {
      return {
        status: 404,
        message: 'Orçamento não encontrado.',
      }
    }

    store.dispatch('financialPlanning/setBudgetsLoading', true)

    const response = await con.delet(budgets.list(clientId) + `/${budgetId}`)

    store.dispatch('financialPlanning/setBudgetsLoading', false)

    if (response && response.status === 200) {
      const targetClientId =
        clientId || store.getters['financialPlanning/budgetsClientId']

      if (targetClientId) {
        store.dispatch('financialPlanning/removeBudget', {
          budgetId,
          clientId: targetClientId,
        })
      }
    }

    return response || {}
  }

  async importar({ clientId, table, file, save = true } = {}) {
    if (!clientId) {
      return {
        status: 400,
        message: 'Cliente não informado.',
      }
    }

    const payload = { save }

    if (Array.isArray(table)) {
      payload.table = table
    }

    if (file) {
      payload.file = file
    }

    const response = await con.post(budgets.batch(clientId), payload)

    if (response && response.status === 200) {
      await this.carregar({ clientId, forceUpdate: true })
    }

    return response || {}
  }

  async batchUpsert({ clientId, items } = {}) {
    if (!clientId) {
      return {
        status: 400,
        message: 'Cliente não informado.',
      }
    }

    if (!Array.isArray(items) || items.length === 0) {
      return {
        status: 400,
        message: 'Nenhum item de orçamento informado.',
      }
    }

    // BE contract (POST /clients/<id>/orcamento/batch-upsert):
    //   body: { data: [{ id_categoria, id_subcategoria?, valor_orcado }] }
    const payload = { data: items }
    const response = await con.post(budgets.batchUpsert(clientId), payload)

    if (response && response.status === 200) {
      await this.carregar({ clientId, forceUpdate: true })
    }

    return response || {}
  }

  async carregarEvolucaoPatrimonio({ clientId, params = {} } = {}) {
    if (!clientId) {
      return {
        status: 400,
        message: 'Cliente não informado.',
      }
    }

    const query = getQueryString(params)
    const requestUrl = `${budgets.patrimonyEvolution(clientId)}${query}`

    const response = await con.get(requestUrl)

    return response || {}
  }
}

const apiFinancialPlanningBudgets = new APIFinancialPlanningBudgets()
export { apiFinancialPlanningBudgets }
